import { t } from 'i18next';
import * as yup from 'yup';

import { RoutingSettings } from '~hooks/useRoutingSettings/models';

// Numeric fields are kept as strings so the text inputs round-trip cleanly and an
// empty optional bound stays distinguishable from 0 — clearing one must send null.
const requiredWholeNumber = () =>
  yup
    .string()
    .trim()
    .matches(/^\d+$/, t('form_validation_errors.numbers_only'))
    .required(t('form_validation_errors.required_simple'));

// Hours are stored as minutes, so a row written elsewhere can hold a value that is
// not a whole number of hours. Rejecting the decimal would make the form unsavable
// on a value it just loaded.
const requiredHours = () =>
  yup
    .string()
    .trim()
    .matches(/^\d+(\.\d+)?$/, t('form_validation_errors.numbers_only'))
    .required(t('form_validation_errors.required_simple'));

const optionalWholeNumber = () =>
  yup
    .string()
    .trim()
    .matches(/^\d*$/, t('form_validation_errors.numbers_only'))
    .defined();

export const routingSettingsSchema = yup.object().shape({
  departmentId: yup.string().nullable().notRequired(),
  pickupOnsiteMinutes: requiredWholeNumber(),
  dropoffOnsiteMinutes: requiredWholeNumber(),
  interstateDriveHours: requiredHours(),
  interstateTotalHours: requiredHours(),
  intrastateDriveHours: requiredHours(),
  intrastateTotalHours: requiredHours(),
  minLoadsPerTruck: optionalWholeNumber(),
  maxLoadsPerTruck: optionalWholeNumber().test(
    'max-not-below-min',
    t('form_validation_errors.max_not_below_min'),
    (value, context) => {
      const min = context.parent.minLoadsPerTruck?.trim();

      if (!value?.trim() || !min) {
        return true;
      }

      return Number(value) >= Number(min);
    },
  ),
  maxOrdersPerTruck: optionalWholeNumber(),
  requireAllTrucks: yup.boolean().defined(),
  resolveAllowRetruckAccepted: yup.boolean().defined(),
  resolveUnacceptedMoveable: yup.boolean().defined(),
});

export type RoutingSettingsDto = yup.InferType<typeof routingSettingsSchema>;

const optionalBoundToInput = (value: number | null) =>
  value === null ? '' : String(value);

// The fallbacks mirror the routing_settings column defaults: 20 minutes onsite, the
// federal hours-of-service limits of 11 driving in a 14-hour duty window, and the
// state equivalents of 12 and 16.
export const setRoutingSettingsSchemaDefaultValues = (
  departmentId: string | null,
  routingSettings?: RoutingSettings,
): RoutingSettingsDto => ({
  departmentId,
  pickupOnsiteMinutes: String(routingSettings?.pickupOnsiteMinutes ?? 20),
  dropoffOnsiteMinutes: String(routingSettings?.dropoffOnsiteMinutes ?? 20),
  interstateDriveHours: String(routingSettings?.interstateDriveHours ?? 11),
  interstateTotalHours: String(routingSettings?.interstateTotalHours ?? 14),
  intrastateDriveHours: String(routingSettings?.intrastateDriveHours ?? 12),
  intrastateTotalHours: String(routingSettings?.intrastateTotalHours ?? 16),
  minLoadsPerTruck: optionalBoundToInput(routingSettings?.minLoadsPerTruck ?? null),
  maxLoadsPerTruck: optionalBoundToInput(routingSettings?.maxLoadsPerTruck ?? null),
  maxOrdersPerTruck: optionalBoundToInput(routingSettings?.maxOrdersPerTruck ?? null),
  requireAllTrucks: routingSettings?.requireAllTrucks ?? false,
  resolveAllowRetruckAccepted: routingSettings?.resolveAllowRetruckAccepted ?? false,
  resolveUnacceptedMoveable: routingSettings?.resolveUnacceptedMoveable ?? true,
});
